import React, { useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripe } from '../utils/stripe';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';

const openStripePortal = async () => {
  try {
    const functions = getFunctions(undefined, 'us-central1');
    const payment = httpsCallable(functions, 'payment');
    const result = await payment({ action: 'create-portal-session' });
    const { url } = result.data as any;
    window.location.href = url;
  } catch (error: any) {
    console.error('Portal error:', error);
    alert(error?.message || 'Could not open subscription management. Please try again.');
  }
};

interface PaymentFormProps {
  tier: PricingTier;
  onSuccess: (partyId?: string) => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ tier, onSuccess, onCancel }) => {
  const stripeInstance = useStripe();
  const elements = useElements();
  const { user, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripeInstance || !elements || !user) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions(undefined, 'us-central1');
      const createPaymentIntent = httpsCallable(functions, 'payment');
      
      const result = await createPaymentIntent({
        action: 'create-payment-intent',
        type: tier.id,
        amount: tier.price * 100 // Convert to cents
      });

      const { clientSecret } = result.data as any;

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error: stripeError, paymentIntent } = await stripeInstance.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user.email || undefined,
            name: user.displayName || undefined,
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
      } else if (paymentIntent?.status === 'succeeded') {
        // Ensure server-side reconciliation even if webhook is delayed
        try {
          await createPaymentIntent({
            action: 'finalize-payment',
            paymentIntentId: paymentIntent.id,
          });
        } catch (finalizeErr) {
          console.warn('Finalize payment callable failed (will rely on webhook):', finalizeErr);
        }
        // Refresh user data to pick up updated subscription fields
        try { await refreshUserData(); } catch {}
        // Create premium party after successful payment
        try {
          const createParty = httpsCallable(functions, 'party');
          const partyResult = await createParty({
            action: 'create',
            name: 'Premium Karaoke Party',
            settings: {
              maxQueueSize: 999,
              requireAuth: false,
              tier: 'party-pass',
              duration: 12 * 60 * 60 * 1000 // 12 hours in milliseconds
            }
          });
          
          const { partyId } = partyResult.data as any;
          onSuccess(partyId);
        } catch (partyError) {
          console.error('Error creating party after payment:', partyError);
          onSuccess(); // Still call success since payment worked
        }
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full">
        <h2 className="text-2xl font-bold mb-6">Complete Your Purchase</h2>
        
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900">{tier.name}</h3>
          <p className="text-2xl font-bold text-qr-primary">${tier.price}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Information
            </label>
            <div className="border border-gray-300 rounded-lg p-3">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripeInstance || loading}
              className="flex-1 py-3 bg-qr-primary hover:bg-qr-secondary text-white font-semibold rounded-lg disabled:opacity-50"
            >
              {loading ? 'Processing...' : `Pay $${tier.price}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface PricingTier {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  popular?: boolean;
}

const SubscriptionPage: React.FC = () => {
  const { user, signInWithGoogle } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  const subscription = useSubscription();

  const pricingTiers: PricingTier[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Perfect for trying out QrOke',
      features: [
        'Up to 5 songs in queue',
        'Basic features',
        'Banner ads',
        '1 hour sessions'
      ]
    },
    {
      id: 'party-pass',
      name: 'Party Pass',
      price: 2,
      description: 'One-time payment for a single party',
      features: [
        '12 hours of premium access',
        'Unlimited queue size',
        'No ads',
        'Custom party branding',
        'Venue ads between songs',
        'Perfect for special events'
      ]
    },
    {
      id: 'pro-subscription',
      name: 'Party Pass Monthly',
      price: 5,
      description: 'Unlimited parties, recurring subscription',
      features: [
        'Unlimited 12-hour parties',
        'All Party Pass features',
        'No ads ever',
        'Custom branding',
        'Best value for regular use'
      ],
      popular: true
    },
    {
      id: 'contact-us',
      name: 'For Venues & Pros',
      price: 0,
      description: 'Custom solutions for venues and professionals',
      features: [
        'Custom branding & themes',
        'Venue-specific features',
        'Professional support',
        'Volume pricing'
      ]
    }
  ];

  const handlePurchase = async (tier: PricingTier) => {
    if (tier.id === 'contact-us') {
      window.location.href = 'mailto:bkentner@gmail.com?subject=QrOke%20Pro&body=Hi%2C%20I\'m%20interested%20in%20QrOke%20for%20my%20venue%2Fevent.%20Please%20contact%20me%20about%20custom%20solutions.';
      return;
    }

    if (!user) {
      toast('Please sign in to make a purchase. Use the Sign In button in the header.', 'warning');
      return;
    }

    // Guard: Pro already includes Party Pass
    if (tier.id === 'party-pass' && subscription.tier === 'pro-subscription' && subscription.isActive) {
      toast('You already have Pro. Party Pass is included.', 'info');
      navigate('/dashboard');
      return;
    }

    // Guard: Active Party Pass
    if (tier.id === 'party-pass' && subscription.tier === 'party-pass' && subscription.isActive) {
      toast('You already have an active Party Pass.', 'info');
      navigate('/dashboard');
      return;
    }

    if (tier.id === 'free') {
      // Create free party directly
      try {
        const functions = getFunctions(undefined, 'us-central1');
        const createParty = httpsCallable(functions, 'party');
        
        const result = await createParty({
          action: 'create',
          name: 'Free Karaoke Party',
          settings: {
            maxQueueSize: 5,
            requireAuth: false,
            tier: 'free'
          }
        });
        
        const { partyId } = result.data as any;
        navigate(`/party/${partyId}`);
      } catch (error) {
        console.error('Error creating free party:', error);
        toast('Failed to create party. Please try again.', 'error');
      }
      return;
    }

    // Use Stripe Payment Link for Party Pass (one-time)
    if (tier.id === 'party-pass') {
      window.location.href = 'https://buy.stripe.com/8x25kD2vW5kTbbW92c0gw0h';
      return;
    }

    // Use Stripe Payment Link for Party Pass Monthly (recurring subscription)
    if (tier.id === 'pro-subscription') {
      window.location.href = 'https://buy.stripe.com/bJe8wPgmMeVt4Ny3HS0gw04';
      return;
    }

    setSelectedTier(tier);
  };

  const handlePaymentSuccess = (partyId?: string) => {
    setSelectedTier(null);
    if (partyId) {
      toast('Payment successful! Redirecting to your premium party...', 'success');
      navigate(`/party/${partyId}`);
    } else {
      toast('Payment successful! Your subscription has been activated.', 'success');
      navigate('/dashboard');
    }
  };

  const handlePaymentCancel = () => {
    setSelectedTier(null);
  };

  const SubscriptionPageContent = () => (
    <div className="min-h-screen p-6 bg-qr-light text-qr-dark">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold mb-4">Choose Your QrOke Plan</h1>
          <p className="text-xl text-gray-600">
            Upgrade your karaoke experience with premium features
          </p>
        </div>

        {/* Current membership status banner */}
        <div className="max-w-3xl mx-auto mb-8">
          {subscription.tier === 'pro-subscription' && subscription.isActive && (
            <div className="p-4 rounded-xl border bg-purple-50 border-purple-200 text-purple-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">You are on Pro</div>
                  {subscription.timeRemaining && (
                    <div className="text-sm">Renews in {subscription.timeRemaining}</div>
                  )}
                </div>
                <button
                  onClick={openStripePortal}
                  className="text-sm bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Manage Subscription
                </button>
              </div>
            </div>
          )}
          {subscription.tier === 'party-pass' && subscription.isActive && (
            <div className="p-4 rounded-xl border bg-blue-50 border-blue-200 text-blue-800">
              <div className="flex items-center justify-between">
                <div className="font-semibold">Party Pass Active</div>
                {subscription.timeRemaining && (
                  <div className="text-sm">Time remaining: {subscription.timeRemaining}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-white rounded-2xl p-8 shadow-lg ${
                tier.popular ? 'border-2 border-qr-primary' : 'border border-gray-200'
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-qr-primary px-4 py-1 rounded-full text-white text-sm font-semibold">
                  Most Popular
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {tier.id === 'contact-us' ? 'Contact Us' : `$${tier.price}`}
                  {tier.id === 'pro-subscription' && <span className="text-lg">/month</span>}
                </div>
                <p className="text-gray-600">{tier.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-3">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              {(() => {
                const disabled =
                  (tier.id === 'party-pass' && subscription.tier === 'pro-subscription' && subscription.isActive) ||
                  (tier.id === 'party-pass' && subscription.tier === 'party-pass' && subscription.isActive) ||
                  (tier.id === 'pro-subscription' && subscription.tier === 'pro-subscription' && subscription.isActive);
                const label = (() => {
                  if (tier.id === 'free') return 'Start Free Party';
                  if (tier.id === 'contact-us') return 'Contact Us';
                  if (tier.id === 'party-pass') {
                    if (subscription.tier === 'pro-subscription' && subscription.isActive) return 'Included in Pro';
                    if (subscription.tier === 'party-pass' && subscription.isActive) return 'Party Pass Active';
                    return 'Get Party Pass';
                  }
                  if (tier.id === 'pro-subscription') {
                    if (subscription.tier === 'pro-subscription' && subscription.isActive) return 'Current Plan';
                    return 'Subscribe Monthly';
                  }
                  return 'Get Started';
                })();
                return (
                  <button
                    onClick={() => handlePurchase(tier)}
                    disabled={disabled}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                      tier.id === 'free'
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : tier.popular
                        ? 'bg-qr-primary hover:bg-qr-secondary text-white'
                        : disabled
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    {label}
                  </button>
                );
              })()}
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Party Pass vs Party Pass Monthly?</h3>
              <p className="text-gray-600">
                <strong>Party Pass ($2 one-time)</strong> gives you one 12-hour party session - perfect for a special event or trying out premium features.<br/><br/>
                <strong>Party Pass Monthly ($5/month)</strong> gives you unlimited 12-hour party sessions throughout the month. Host as many parties as you want! Best value for regular karaoke hosts.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">What if I need custom features for my venue?</h3>
              <p className="text-gray-600">
                For bars, restaurants, and professional KJ events, we offer custom solutions including 
                venue-specific branding, advanced features, and volume pricing. Contact us to discuss your needs.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Is my payment information secure?</h3>
              <p className="text-gray-600">
                Absolutely! We use Stripe for payment processing, which is PCI compliant and trusted 
                by millions of businesses worldwide. We never store your payment information.
              </p>
            </div>
          </div>
        </div>

        {selectedTier && (
          <PaymentForm
            tier={selectedTier}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
          />
        )}
      </div>
    </div>
  );

  return (
    <Elements stripe={stripe}>
      <SubscriptionPageContent />
    </Elements>
  );
};

export default SubscriptionPage;